import { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  ButtonRouterLink,
  Checkbox,
  Modal,
  MonoLabel,
  NavListItem,
  Select,
  Skeleton,
  Tag,
  TextAreaField,
  TextField,
  Toast
} from '../components/primitives';
import {
  groupCapabilitiesByResource,
  resolveCapabilityKeys,
  type PermissionGroup
} from '../lib/models/permissionGroups';
import {
  useSettingsPermissionGroupsVM,
  type CloneFromOption
} from '../lib/vm/useSettingsPermissionGroupsVM';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import './SettingsPermissionGroups.css';

const OWNER_NOTE =
  'Owner always has every capability. This group cannot be edited.';
const SYSTEM_NOTE =
  'System groups are read-only. Create a custom group cloned from this one to customize its capabilities.';
const CUSTOM_READONLY_NOTE =
  'Custom groups can be created, edited, and deleted by Owners.';
const ADMIN_READONLY_NOTE =
  'You can view permission groups. Only Owners can change them.';

function memberCountLabel(n: number): string {
  return `${n} member${n === 1 ? '' : 's'}`;
}

/**
 * `/settings/permission-groups` (turn 6.2 — read-only view).
 *
 * Two-column layout: left group list (System = Owner/Admin/Member + member
 * counts; Custom = org-defined groups), right read-only editor with the
 * capability matrix grouped by resource section. Owners + Admins view;
 * Members are bounced to a no-access surface. Create / edit / delete land
 * in 6.3.
 */
export function SettingsPermissionGroups() {
  const vm = useSettingsPermissionGroupsVM();

  return (
    <section
      className="set-pg-page"
      data-testid="settings-permission-groups"
      data-status={vm.status}
      data-viewer-role={vm.viewerRole ?? ''}
    >
      <SettingsBackLink />
      {vm.status === 'loading' ? (
        <div className="set-pg-loading" data-testid="settings-pg-loading">
          <Skeleton height={48} />
          <Skeleton height={320} />
        </div>
      ) : null}

      {vm.status === 'error' ? (
        <Banner kind="error" data-testid="settings-pg-error">
          {vm.error ?? "Couldn't load permission groups. Try again."}
        </Banner>
      ) : null}

      {vm.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-pg-unauth">
          You need to sign in to view permission groups.
        </Banner>
      ) : null}

      {vm.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-pg-empty">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'forbidden' ? (
        <div className="set-pg-no-access" data-testid="settings-pg-forbidden">
          <h2 className="set-pg-no-access-title">You don't have access</h2>
          <p className="set-pg-no-access-body">
            Only Owners and Admins can view permission groups. Ask an Owner if
            you need to manage permissions.
          </p>
          <ButtonRouterLink to="/dashboard" variant="ghost">
            Back to dashboard
          </ButtonRouterLink>
        </div>
      ) : null}

      {vm.status === 'ready' ? <ReadyView vm={vm} /> : null}
    </section>
  );
}

function ReadyView({
  vm
}: {
  vm: ReturnType<typeof useSettingsPermissionGroupsVM>;
}) {
  return (
    <>
      <header className="set-pg-head">
        <h1 className="set-pg-title">Permission groups</h1>
        <p className="set-pg-lede">
          Define what each group of users in your org can do. Owner / Admin /
          Member are system groups (read-only). Custom groups grant specific
          capabilities to subsets of members.
        </p>
      </header>

      <div className="set-pg-grid">
        <aside className="set-pg-list" data-testid="settings-pg-list">
          <GroupSection
            title="System"
            groups={vm.systemGroups}
            selectedId={vm.selectedId}
            onSelect={vm.actions.select}
            testid="settings-pg-system"
          />
          <GroupSection
            title="Custom"
            groups={vm.customGroups}
            selectedId={vm.selectedId}
            onSelect={vm.actions.select}
            emptyNote="No custom groups yet."
            testid="settings-pg-custom"
          />
          {vm.canManage ? (
            <div className="set-pg-create">
              <Button
                variant="ghost"
                onClick={vm.actions.openCreate}
                data-testid="settings-pg-create-trigger"
              >
                + Create group
              </Button>
            </div>
          ) : null}
        </aside>

        <div className="set-pg-editor" data-testid="settings-pg-editor">
          {vm.selectedGroup ? (
            <GroupEditor vm={vm} />
          ) : (
            <p className="set-pg-empty-note">Select a group to view it.</p>
          )}
        </div>
      </div>

      <CreateGroupModal vm={vm} />

      {vm.saveToast ? (
        <Toast title="Saved" onDismiss={vm.actions.clearSaveToast}>
          {vm.saveToast}
        </Toast>
      ) : null}

      {vm.deleteToast ? (
        <Toast
          title="Group deleted"
          durationMs={6000}
          onDismiss={vm.actions.clearDeleteToast}
        >
          {`Deleted “${vm.deleteToast}”.`}
        </Toast>
      ) : null}
    </>
  );
}

const CLONE_FROM_OPTIONS = [
  { value: '', label: 'Blank' },
  { value: 'member', label: 'Clone from Member' },
  { value: 'admin', label: 'Clone from Admin' }
];

function CreateGroupModal({
  vm
}: {
  vm: ReturnType<typeof useSettingsPermissionGroupsVM>;
}) {
  const open = vm.createStatus !== 'closed';
  const saving = vm.createStatus === 'saving';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cloneFrom, setCloneFrom] = useState<CloneFromOption>('');

  // Reset the draft each time the modal opens.
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setCloneFrom('');
    }
  }, [open]);

  const err = vm.createError;
  const nameError = err?.field === 'name' ? err.message : undefined;
  const descriptionError =
    err?.field === 'description' ? err.message : undefined;
  const formError = err?.field === 'form' ? err.message : undefined;

  const submit = () => {
    void vm.actions.createGroup({ name, description, cloneFrom });
  };

  return (
    <Modal
      open={open}
      onClose={vm.actions.closeCreate}
      title="Create a custom permission group"
      dataTestId="settings-pg-create-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={vm.actions.closeCreate}
            data-testid="settings-pg-create-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={saving}
            data-testid="settings-pg-create-submit"
          >
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        className="set-pg-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {formError ? (
          <Banner kind="error" data-testid="settings-pg-create-form-error">
            {formError}
          </Banner>
        ) : null}
        <TextField
          id="settings-pg-create-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          placeholder="e.g. QA-Lead"
          autoComplete="off"
          helpText="2–40 chars: letters, numbers, spaces, dashes. Must be unique in your org."
          errorText={nameError}
          data-testid="settings-pg-create-name"
        />
        <TextAreaField
          id="settings-pg-create-description"
          label="Description (optional, ≤ 280 chars)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={280}
          rows={3}
          errorText={descriptionError}
          data-testid="settings-pg-create-description"
        />
        <Select
          id="settings-pg-create-clone"
          label="Starting point"
          value={cloneFrom}
          onChange={(e) => setCloneFrom(e.target.value as CloneFromOption)}
          options={CLONE_FROM_OPTIONS}
          helpText="Clone copies the source group's capabilities as the initial matrix."
          data-testid="settings-pg-create-clone"
        />
      </form>
    </Modal>
  );
}

function GroupSection({
  title,
  groups,
  selectedId,
  onSelect,
  emptyNote,
  testid
}: {
  title: string;
  groups: PermissionGroup[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  emptyNote?: string;
  testid: string;
}) {
  return (
    <div className="set-pg-section" data-testid={testid}>
      <MonoLabel
        as="div"
        size="md"
        tracking="wide"
        className="set-pg-section-head"
      >
        {title}
      </MonoLabel>
      {groups.length === 0 && emptyNote ? (
        <p className="set-pg-section-empty">{emptyNote}</p>
      ) : (
        groups.map((g) => (
          <NavListItem
            key={g.id}
            label={g.name}
            meta={memberCountLabel(g.memberCount)}
            selected={g.id === selectedId}
            onClick={() => onSelect(g.id)}
            data-testid={`settings-pg-group-${g.id}`}
          />
        ))
      )}
    </div>
  );
}

function GroupEditor({
  vm
}: {
  vm: ReturnType<typeof useSettingsPermissionGroupsVM>;
}) {
  const group = vm.selectedGroup as PermissionGroup;
  const isSystem = group.kind === 'system';
  const isOwnerGroup = isSystem && group.systemKey === 'owner';
  // Owner viewers can edit custom groups; system groups + Admin viewers
  // stay read-only.
  const editable = !isSystem && vm.canManage;
  const sections = groupCapabilitiesByResource(vm.capabilities);

  const draft = vm.draft;
  const draftName = draft?.name ?? group.name;
  const draftDescription = draft?.description ?? group.description ?? '';
  const checkedKeys = draft
    ? new Set(draft.capabilityKeys)
    : resolveCapabilityKeys(group, vm.capabilities);

  const note = isOwnerGroup
    ? OWNER_NOTE
    : isSystem
      ? SYSTEM_NOTE
      : vm.canManage
        ? CUSTOM_READONLY_NOTE
        : ADMIN_READONLY_NOTE;

  const saveErr = vm.saveError;
  const nameError = saveErr?.field === 'name' ? saveErr.message : undefined;
  const descriptionError =
    saveErr?.field === 'description' ? saveErr.message : undefined;
  const formError = saveErr?.field === 'form' ? saveErr.message : undefined;

  return (
    <div className="set-pg-editor-body" data-testid="settings-pg-editor-body">
      <div className="set-pg-editor-head">
        <Tag>{isSystem ? 'System' : 'Custom'}</Tag>
        <h2
          className="set-pg-editor-title"
          data-testid="settings-pg-editor-title"
        >
          {group.name}
        </h2>
      </div>
      <p
        className="set-pg-editor-meta"
        data-testid="settings-pg-editor-membercount"
      >
        {memberCountLabel(group.memberCount)}
      </p>

      {editable ? (
        <div className="set-pg-editor-fields">
          <TextField
            id="settings-pg-editor-name"
            label="Name"
            value={draftName}
            onChange={(e) => vm.actions.editDraft({ name: e.target.value })}
            maxLength={40}
            autoComplete="off"
            helpText="2–40 chars: letters, numbers, spaces, dashes. Must be unique in your org."
            errorText={nameError}
            data-testid="settings-pg-editor-name"
          />
          <TextAreaField
            id="settings-pg-editor-description"
            label="Description (optional, ≤ 280 chars)"
            value={draftDescription}
            onChange={(e) =>
              vm.actions.editDraft({ description: e.target.value })
            }
            maxLength={280}
            rows={3}
            errorText={descriptionError}
            data-testid="settings-pg-editor-description"
          />
        </div>
      ) : null}

      <div className="set-pg-matrix" data-testid="settings-pg-matrix">
        {sections.map((section) => (
          <div
            key={section.resource}
            className="set-pg-matrix-section"
            data-testid={`settings-pg-section-${section.resource}`}
          >
            <MonoLabel
              as="div"
              size="sm"
              tracking="wide"
              className="set-pg-matrix-title"
            >
              {section.title}
            </MonoLabel>
            <div className="set-pg-matrix-rows">
              {section.capabilities.map((cap) =>
                editable ? (
                  <Checkbox
                    key={cap.key}
                    label={cap.label}
                    hint={cap.key}
                    checked={checkedKeys.has(cap.key)}
                    onChange={() => vm.actions.toggleCapability(cap.key)}
                    data-testid={`settings-pg-cap-${cap.key}`}
                  />
                ) : (
                  <Checkbox
                    key={cap.key}
                    label={cap.label}
                    hint={cap.key}
                    checked={checkedKeys.has(cap.key)}
                    disabled
                    readOnly
                    data-testid={`settings-pg-cap-${cap.key}`}
                  />
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {editable && formError ? (
        <Banner kind="error" data-testid="settings-pg-editor-save-error">
          {formError}
        </Banner>
      ) : null}

      {editable && vm.isDirty ? (
        <div
          className="set-pg-editor-actions"
          data-testid="settings-pg-editor-actions"
        >
          <Button
            variant="ghost"
            onClick={vm.actions.cancelEdit}
            data-testid="settings-pg-editor-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void vm.actions.saveGroup()}
            disabled={vm.saveStatus === 'saving'}
            data-testid="settings-pg-editor-save"
          >
            {vm.saveStatus === 'saving' ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : null}

      {editable ? <DeletePanel vm={vm} group={group} /> : null}

      <Banner kind="default" data-testid="settings-pg-editor-note">
        {note}
      </Banner>
    </div>
  );
}

function DeletePanel({
  vm,
  group
}: {
  vm: ReturnType<typeof useSettingsPermissionGroupsVM>;
  group: PermissionGroup;
}) {
  const { deleteStatus } = vm;
  const [confirmName, setConfirmName] = useState('');

  // Clear the typed-name draft whenever the panel opens, closes, or the
  // selected group changes.
  useEffect(() => {
    setConfirmName('');
  }, [deleteStatus, group.id]);

  if (deleteStatus === 'idle') {
    return (
      <div className="set-pg-delete">
        <Button
          variant="ghost"
          onClick={vm.actions.openDelete}
          data-testid="settings-pg-delete-trigger"
        >
          Delete group
        </Button>
      </div>
    );
  }

  if (deleteStatus === 'blocked') {
    return (
      <div className="set-pg-delete" data-testid="settings-pg-delete-blocked">
        <Banner kind="error">
          {`${memberCountLabel(group.memberCount)} ${
            group.memberCount === 1 ? 'is' : 'are'
          } in ${group.name}. Reassign them first.`}
        </Banner>
        <div className="set-pg-delete-actions">
          <Button
            variant="ghost"
            onClick={vm.actions.closeDelete}
            data-testid="settings-pg-delete-cancel"
          >
            Cancel
          </Button>
          <ButtonRouterLink
            to="/members"
            variant="primary"
            data-testid="settings-pg-delete-open-members"
          >
            Open Members →
          </ButtonRouterLink>
        </div>
      </div>
    );
  }

  const deleting = deleteStatus === 'deleting';
  const matches = confirmName === group.name;

  return (
    <div className="set-pg-delete" data-testid="settings-pg-delete-confirm">
      <p className="set-pg-delete-prompt">
        Type <strong>{group.name}</strong> to confirm deletion. This can't be
        undone.
      </p>
      {vm.deleteError ? (
        <Banner kind="error" data-testid="settings-pg-delete-error">
          {vm.deleteError}
        </Banner>
      ) : null}
      <TextField
        id="settings-pg-delete-input"
        label="Group name"
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        autoComplete="off"
        data-testid="settings-pg-delete-input"
      />
      <div className="set-pg-delete-actions">
        <Button
          variant="ghost"
          onClick={vm.actions.closeDelete}
          disabled={deleting}
          data-testid="settings-pg-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void vm.actions.deleteGroup(confirmName)}
          disabled={!matches || deleting}
          data-testid="settings-pg-delete-submit"
        >
          {deleting ? 'Deleting…' : 'Delete group'}
        </Button>
      </div>
    </div>
  );
}
